import { Box, IconButton, TableCell, TableRow } from '@material-ui/core';
import { formatMoney, Item, OrderDetails } from '@nemuru-eng/js-api-client';

import { useOrderDetails } from '@/components/modules/Cart/components/OrderLines/hooks/useOrderDetails';
import { MoreActionsMenu } from '@/components/shared/components/MoreActionsMenu/MoreActionsMenu';
import { MoreActionsMenuWithIcon } from '@/components/shared/components/MoreActionsMenu/MoreActionsMenuWithIcon';
import { StatusTag } from '@/components/shared/components/StatusTag/StatusTag';
import { Font } from '@/components/shared/components/Font';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { getIconByArticleType } from '@/utils/getIconByArticleType';

export const ItemRow = ({
  orderDetails,
  item,
  itemIndex,
  showItemDetails,
  isMobile,
  tableProps,
}: {
  orderDetails: OrderDetails;
  item: Item;
  itemIndex: number;
  showItemDetails: (index: number) => void;
  isMobile: boolean;
  tableProps: { classes; stickyCellStyle };
}) => {
  const { palette } = useCustomTheme();
  const { classes, stickyCellStyle } = tableProps;
  const { getOrderItemStatus } = useOrderDetails(orderDetails);
  const currency = orderDetails.currency;

  const actionsOptionsArrayByIndex = (index) => {
    return [
      {
        display: <Text tId={'SEE_DETAIL'} />,
        iconName: 'search',
        onClick: () => showItemDetails(index),
      },
    ];
  };

  const desktopItemRow = (
    <TableRow key={itemIndex} className={classes.root}>
      <TableCell>
        <Box width={'100%'} display={'inline-flex'} alignItems={'center'} justifyContent={'space-between'}>
          <Box display={'flex'}>
            <i className="material-icons-outlined" style={{ fontSize: 18, color: palette.grey[400], paddingRight: 8 }}>
              {getIconByArticleType(item.article.type)}
            </i>
            <Font variant={'body2'} color={'textPrimary'}>
              {item.article.name}
            </Font>
          </Box>
          <Box pl={1}>
            <StatusTag type={getOrderItemStatus(item)?.statusColor} labelTextId={getOrderItemStatus(item)?.status} />
          </Box>
        </Box>
      </TableCell>
      <TableCell align="right">{item.units} x</TableCell>
      <TableCell align="right">{formatMoney({ value: item.article.unitPriceWithTax, currency })}</TableCell>
      <TableCell align="right">{formatMoney({ value: item.totalPriceWithTax, currency })}</TableCell>
      <TableCell align="right">
        <MoreActionsMenuWithIcon actionsOptionsArray={actionsOptionsArrayByIndex(itemIndex)} />
      </TableCell>
    </TableRow>
  );

  const mobileItemRow = (
    <MoreActionsMenu
      actionsOptionsArray={actionsOptionsArrayByIndex(itemIndex)}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      <TableRow key={itemIndex} className={classes.root}>
        <TableCell style={stickyCellStyle}>
          <Box
            width={'100%'}
            display={'flex'}
            flexDirection={'row'}
            alignItems={'center'}
            justifyContent={'space-between'}
          >
            <Box display={'flex'} alignItems={'center'}>
              <i
                className="material-icons-outlined"
                style={{ fontSize: 18, color: palette.grey[400], paddingRight: 8 }}
              >
                {getIconByArticleType(item.article.type)}
              </i>
              <Font variant={'body2'} color={'textPrimary'}>
                {item.article.name}
              </Font>
            </Box>
          </Box>
        </TableCell>
        <TableCell style={{ minWidth: 80 }}>
          <StatusTag type={getOrderItemStatus(item)?.statusColor} labelTextId={getOrderItemStatus(item)?.status} />
        </TableCell>
        <TableCell align="center" style={{ minWidth: 50 }}>
          {item.units} x
        </TableCell>
        <TableCell align="center" style={{ minWidth: 80 }}>
          {formatMoney({ value: item.article.unitPriceWithTax, currency })}
        </TableCell>
        <TableCell align="center" style={{ minWidth: 80 }}>
          {formatMoney({ value: item.totalPriceWithTax, currency })}
        </TableCell>
        <TableCell align="center" style={{ minWidth: 40 }}>
          <IconButton size={'small'} className={'ml-2'}>
            {<i className="material-icons-outlined">more_horiz</i>}
          </IconButton>
        </TableCell>
      </TableRow>
    </MoreActionsMenu>
  );

  return isMobile ? mobileItemRow : desktopItemRow;
};
